import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apple, Droplet, Coffee, Cookie, Sparkles, ChevronRight, Heart } from 'lucide-react';
import { api } from './api';
import { MaternalBackLink, MaternalDisclaimer, MaternalLoader } from './_ui';

/**
 * Maternal · Nutrition focus
 *
 * Layered view:
 *   1. Today's gentle focus (pulled from /maternal/week.nutrition)
 *   2. Stage-aware nutrient checklist (static, evidence-anchored)
 *   3. "Foods that may be best to avoid" — calm framing, not scary
 *   4. Daily hydration & balanced-meal nudge
 *
 * No diagnosis, no calorie counts, no shaming. Tone matches the rest of
 * the maternal companion.
 */

const NUTRIENT_FOCUS = {
  t1: [
    { name: 'Folic acid', why: '400–600 mcg daily — supports baby\'s neural tube development.', sources: 'Leafy greens, lentils, fortified cereal, oranges' },
    { name: 'B6', why: 'Helps with first-trimester nausea for many people.', sources: 'Bananas, chicken, potatoes, oats' },
    { name: 'Iron', why: 'Your blood volume is increasing — iron supports it.', sources: 'Red meat, beans, lentils + a vitamin-C food' },
    { name: 'Hydration', why: '6–8 cups of water/fluids spread through the day.', sources: 'Water, milk, herbal teas, soups, fruit' },
  ],
  t2: [
    { name: 'Iron + protein', why: 'Steady energy as your body works harder.', sources: 'Beans, eggs, fish, lean meat, tofu' },
    { name: 'Calcium', why: 'Baby\'s bones are forming.', sources: 'Yogurt, milk, fortified plant drinks, sardines' },
    { name: 'Omega-3 (DHA)', why: 'Supports baby\'s brain and eye development.', sources: 'Oily fish (low-mercury), walnuts, flax, chia' },
    { name: 'Whole grains', why: 'Steady blood sugar = steady energy.', sources: 'Oats, brown rice, whole-grain bread, quinoa' },
  ],
  t3: [
    { name: 'Smaller frequent meals', why: 'Easier on heartburn and digestion.', sources: '4–6 mini meals instead of 3 big ones' },
    { name: 'Magnesium', why: 'Helps with cramps and sleep.', sources: 'Nuts, seeds, dark leafy greens, dark chocolate' },
    { name: 'Hydration', why: 'Especially important now.', sources: 'Water, milk, soups, hydrating fruit' },
    { name: 'Iron-rich foods', why: 'Birth-prep for steady energy and recovery.', sources: 'Liver (if comfortable), beans, fortified cereals' },
  ],
  postpartum: [
    { name: 'Iron + B12', why: 'Recovery from blood loss.', sources: 'Eggs, fish, fortified cereals, leafy greens' },
    { name: 'Hydration', why: 'Especially if breastfeeding.', sources: 'Water, milk, herbal teas, broths' },
    { name: 'Healthy fats', why: 'Supports milk supply and mood.', sources: 'Avocado, olive oil, nuts, oily fish' },
    { name: 'Protein at every meal', why: 'Tissue repair and steady energy.', sources: 'Eggs, beans, fish, lean meat, dairy' },
  ],
  recovery: [
    { name: 'Steady balanced meals', why: 'Re-establishing your own rhythm.', sources: 'Half plate veg, palm of protein, fist of grains' },
    { name: 'Iron-rich foods', why: 'Especially around your cycle returning.', sources: 'Beans, dark greens, lean meat, fortified grains' },
    { name: 'Calcium + vitamin D', why: 'Bone health continues to matter.', sources: 'Yogurt, milk, fortified drinks, sunshine' },
    { name: 'Hydration', why: '7–9 cups daily — easy daily anchor.', sources: 'Water, milk, herbal teas' },
  ],
  trying: [
    { name: 'Folic acid', why: 'Start now — protects neural-tube development if you conceive.', sources: 'Leafy greens, lentils, fortified cereal' },
    { name: 'Balanced plate', why: 'Cycle regularity loves steady meals.', sources: 'Half plate veg, palm of protein, fist of grains' },
    { name: 'Hydration', why: '6–8 cups daily — energy + cycle.', sources: 'Water, milk, herbal teas' },
    { name: 'Whole grains', why: 'Steady blood-sugar supports hormone balance.', sources: 'Oats, brown rice, whole-grain bread' },
  ],
};

// Calm "best to avoid" — framed gently, not as panic.
const AVOID_BY_STAGE = {
  pregnancy: [
    'Raw fish and undercooked meat',
    'Soft unpasteurised cheeses',
    'High-mercury fish (shark, swordfish, marlin)',
    'Alcohol',
    'More than 200 mg caffeine/day',
  ],
  postpartum: [
    'Alcohol while breastfeeding (or time intake far from feeds)',
    'High-mercury fish if breastfeeding',
    'Restrictive crash diets — recovery needs nourishment',
  ],
};

const STAGE_TITLES = {
  t1: 'First trimester · weeks 1–12',
  t2: 'Second trimester · weeks 13–27',
  t3: 'Third trimester · weeks 28+',
  postpartum: 'Postpartum recovery',
  recovery: 'Long-term recovery',
  trying: 'Trying to conceive',
  unset: 'Set your stage to see nutrition tips',
};

const MaternalNutrition = () => {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.snapshot().catch(() => null),
      api.weekInsight().catch(() => ({ data: null })),
    ])
      .then(([s, w]) => { setSnap(s); setWeek(w?.data || null); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <MaternalLoader label="Loading nutrition tips…" testid="maternal-nutrition-loading" />;
  }

  const stage = snap?.stage_info?.stage || 'unset';
  const isPregnant = ['t1', 't2', 't3'].includes(stage);
  const isPP = ['postpartum', 'recovery'].includes(stage);
  const focus = NUTRIENT_FOCUS[stage] || [];
  const avoidList = isPregnant ? AVOID_BY_STAGE.pregnancy : (isPP ? AVOID_BY_STAGE.postpartum : []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-nutrition">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shrink-0">
              <Apple className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest">Nutrition focus</p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight" data-testid="maternal-nutrition-title">
                {STAGE_TITLES[stage] || STAGE_TITLES.unset}
              </h1>
            </div>
          </div>
        </header>

        {/* Today's gentle focus from /week */}
        {week?.nutrition && (
          <article
            className="rounded-2xl bg-white border-2 border-emerald-100 shadow-sm p-5 mb-4"
            data-testid="maternal-nutrition-week"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-semibold text-emerald-800 uppercase tracking-widest mb-1">This week</p>
                <p className="text-sm text-gray-800 leading-relaxed">{week.nutrition}</p>
              </div>
            </div>
          </article>
        )}

        {/* Stage-aware nutrient checklist */}
        {focus.length > 0 && (
          <section className="space-y-3 mb-4" data-testid="maternal-nutrition-focus">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1 px-1">
              What to lean on
            </p>
            {focus.map((f) => (
              <article
                key={f.name}
                className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4"
                data-testid={`maternal-nutrition-item-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                    <Heart className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 leading-tight">{f.name}</p>
                    <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{f.why}</p>
                    <p className="text-xs text-gray-500 leading-relaxed mt-1.5">
                      <span className="font-semibold text-gray-700">Foods:</span> {f.sources}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}

        {/* Hydration + meal nudge */}
        <section
          className="rounded-2xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 p-4 mb-4"
          data-testid="maternal-nutrition-daily"
        >
          <p className="text-[11px] font-semibold text-sky-800 uppercase tracking-widest mb-2">Today's anchors</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-gray-800">
              <Droplet className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" />
              <span>{isPP ? '8–10' : '6–9'} cups of fluids spread through the day — sip, don't gulp.</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-800">
              <Cookie className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>Half plate vegetables · palm of protein · fist of whole grains. Repeat at every meal.</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-800">
              <Coffee className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
              <span>Caffeine: keep total under {isPregnant ? '200 mg' : '300 mg'} a day (~1–2 small coffees).</span>
            </li>
          </ul>
        </section>

        {/* Calm "best to avoid" */}
        {avoidList.length > 0 && (
          <section
            className="rounded-2xl bg-white border border-amber-200 p-4 mb-4"
            data-testid="maternal-nutrition-avoid"
          >
            <p className="text-[11px] font-semibold text-amber-800 uppercase tracking-widest mb-2">Best to keep light</p>
            <ul className="space-y-1.5">
              {avoidList.map((a) => (
                <li key={a} className="text-sm text-gray-800 leading-relaxed flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-2" aria-hidden="true" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-gray-500 italic mt-2">
              Always check with your clinician about supplements and any specific dietary needs.
            </p>
          </section>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/maternal-health/checkin')}
            className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
            data-testid="maternal-nutrition-checkin-cta"
          >
            Log today's check-in
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => navigate('/maternal-health/week')}
            className="inline-flex items-center gap-1.5 bg-white border-2 border-gray-200 hover:border-rose-300 text-gray-800 text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
          >
            See this week's guidance
          </button>
        </div>

        <MaternalDisclaimer />
      </div>
    </div>
  );
};

export default MaternalNutrition;
